/**
 * Token manager for handling token updates and request queuing
 */

type PendingRequest = {
  id: string
  resolvers: { resolve: (value: any) => void; reject: (error: any) => void }[]
  url: string
  options: RequestInit
  retryCount: number
}

class TokenManager {
  private pendingRequests: PendingRequest[] = []
  private isWaitingForToken = false
  private tokenUpdateListeners: ((token: string | null) => void)[] = []
  private tokenRequested = false // Flag to prevent multiple token requests

  // Add a listener for token updates
  addTokenUpdateListener(listener: (token: string | null) => void) {
    this.tokenUpdateListeners.push(listener)
    return () => {
      const index = this.tokenUpdateListeners.indexOf(listener)
      if (index > -1) {
        this.tokenUpdateListeners.splice(index, 1)
      }
    }
  }

  // Notify all listeners when token is updated
  notifyTokenUpdate(token: string | null) {
    console.log("TokenManager: Notifying token update to", this.tokenUpdateListeners.length, "listeners")
    this.tokenUpdateListeners.forEach((listener) => listener(token))

    // Reset the token requested flag since we got a response
    this.tokenRequested = false

    if (token && this.pendingRequests.length > 0) {
      // New token — retry all pending requests
      console.log("TokenManager: Retrying", this.pendingRequests.length, "pending requests")
      this.retryPendingRequests(token)
    } else if (!token && this.pendingRequests.length > 0) {
      // Token refresh failed / logout — reject all pending requests
      console.log("TokenManager: Token refresh failed, rejecting", this.pendingRequests.length, "pending requests")
      const requests = [...this.pendingRequests]
      this.pendingRequests = []
      this.isWaitingForToken = false
      requests.forEach((request) => {
        request.resolvers.forEach(({ reject }) => reject(new Error("Authentication failed")))
      })
    }
  }

  // Generate a unique request ID based on URL and options
  private generateRequestId(url: string, options: RequestInit): string {
    const method = options.method || "GET"
    const body = typeof options.body === "string" ? options.body : (options.body ? JSON.stringify(options.body) : "")
    return `${method}-${url}-${body}`
  }

  // Check if we should request a new token
  shouldRequestToken(): boolean {
    if (this.tokenRequested) {
      console.log("TokenManager: Token already requested, not requesting again")
      return false
    }
    this.tokenRequested = true
    return true
  }

  // Queue a request to be retried when a new token is available
  queueRequest(url: string, options: RequestInit): Promise<Response> {
    return new Promise((resolve, reject) => {
      const requestId = this.generateRequestId(url, options)

      // Check if this exact request is already queued
      const existingRequest = this.pendingRequests.find((req) => req.id === requestId)
      if (existingRequest) {
        console.log("TokenManager: Request already queued, adding caller to existing", url)
        existingRequest.resolvers.push({ resolve, reject })
        return
      }

      console.log("TokenManager: Queueing request for", url)
      this.pendingRequests.push({
        id: requestId,
        resolvers: [{ resolve, reject }],
        url,
        options,
        retryCount: 0,
      })

      // Set flag that we're waiting for a token
      this.isWaitingForToken = true
    })
  }

  // Retry all pending requests with the new token
  private async retryPendingRequests(token: string) {
    if (this.pendingRequests.length === 0) {
      return
    }

    const requests = [...this.pendingRequests]
    this.pendingRequests = []
    this.isWaitingForToken = false

    console.log("TokenManager: Retrying", requests.length, "requests with new token")

    // Group requests by ID to handle duplicates
    const uniqueRequests = new Map<string, PendingRequest>()
    requests.forEach((request) => {
      if (!uniqueRequests.has(request.id)) {
        uniqueRequests.set(request.id, request)
      }
    })

    for (const request of uniqueRequests.values()) {
      try {
        // Update the authorization header with the new token
        const headers = new Headers(request.options.headers)
        headers.set("Authorization", `Bearer ${token}`)

        console.log(`TokenManager: Retrying request for ${request.url}`)

        const response = await fetch(request.url, {
          ...request.options,
          headers,
        })

        // Resolve all callers waiting on this request
        request.resolvers.forEach(({ resolve }) => resolve(response.clone()))
      } catch (error) {
        console.error("TokenManager: Failed to retry request for", request.url, error)
        request.resolvers.forEach(({ reject }) => reject(error))
      }
    }
  }

  // Check if we're currently waiting for a token
  isWaitingForNewToken(): boolean {
    return this.isWaitingForToken
  }

  // Check if a specific request is already queued
  isRequestQueued(url: string, options: RequestInit): boolean {
    const requestId = this.generateRequestId(url, options)
    return this.pendingRequests.some((req) => req.id === requestId)
  }

  // Clear all pending requests (e.g., on component unmount)
  clearPendingRequests() {
    console.log("TokenManager: Clearing", this.pendingRequests.length, "pending requests")
    this.pendingRequests.forEach((request) => {
      request.resolvers.forEach(({ reject }) => reject(new Error("Request cancelled")))
    })
    this.pendingRequests = []
    this.isWaitingForToken = false
    this.tokenRequested = false
  }
}

// Export a singleton instance
export const tokenManager = new TokenManager()
